import { Grid, LinearProgress } from "@material-ui/core";
import { Map, Room } from "@material-ui/icons";
import React, { FC, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import {
  useCreateLocationMutation,
  useDeleteLocationMutation,
  useGetLocationQuery,
  useUpdateLocationMutation,
} from "../../api/rtk/LocationApi";
import { NewEntityId } from "../../data/constants";
import { LocationViewModel } from "../../model/LocationViewModel";
import { routesPaths } from "../../Routes";
import { navigateBack } from "../../utils/Navigation";
import DefaultButtons from "../common/buttons/DefaultButtons";
import LinkButton from "../common/buttons/LinkButton";
import TitleWithIcon from "../common/TitleWithIcon";
import RoomList from "../room/RoomList";
import TranslatedMessage from "../translations/TranslatedMessage";
import { initialLocation } from "./LocationConstants";
import LocationFields from "./LocationFields";

const LocationForm: FC = () => {
  const history = useHistory();
  const { locationId } = useParams<{ locationId: string }>();

  const [location, setLocation] = useState(initialLocation);
  const [isEmailValid, setEmailValid] = useState(false);

  const [deleteLocation, { isLoading: isDeleteLocationLoading, isSuccess: isDeleteSuccess }] =
    useDeleteLocationMutation();
  const [createLocation, { isLoading: isCreateLocationLoading }] = useCreateLocationMutation();
  const [updateLocation, { isLoading: isUpdateLocationLoading }] = useUpdateLocationMutation();
  const { data: fetchedLocation, isFetching: isLocationFetching } = useGetLocationQuery(locationId, {
    skip: locationId === NewEntityId || isDeleteSuccess,
  });
  const isLoading = isLocationFetching || isDeleteLocationLoading || isCreateLocationLoading || isUpdateLocationLoading;

  useEffect(() => {
    if (fetchedLocation !== undefined) {
      setLocation(fetchedLocation);
    }
  }, [fetchedLocation]);

  if (isLoading) {
    return <LinearProgress variant="query" />;
  }

  if (fetchedLocation === undefined && locationId !== NewEntityId) {
    return <></>;
  }

  const close = () => navigateBack(routesPaths.locations(), history, history.location);

  const handleChange = (partialLocation: Partial<LocationViewModel>) =>
    setLocation((prevLocation) => ({ ...prevLocation, ...partialLocation }));

  const handleEmailIsValid = (isEmailValid: boolean) => setEmailValid(isEmailValid);

  const handleClose = () => close();

  const handleSave = async () => {
    if (location.id === NewEntityId) {
      const response = await createLocation(location).unwrap();
      if (response.success) {
        const locationId = response.data;
        history.replace(routesPaths.location(locationId));
      }
    } else {
      updateLocation(location);
    }
  };

  const handleDelete = async () => {
    const response = await deleteLocation(location.id).unwrap();
    if (response.success) {
      close();
    }
  };

  const handleRoomSelected = (roomId: string) => history.push(routesPaths.room(location.id, roomId));

  const handleStartAddingRoom = () => history.push(routesPaths.room(location.id, NewEntityId));

  const additionalButtons = location.linkToMap && (
    <LinkButton tooltip={<TranslatedMessage id="location.field.linkToMap" />} href={location.linkToMap} target="_blank">
      <Map />
    </LinkButton>
  );

  return (
    <Grid container={true} direction="column" spacing={3}>
      <Grid item={true} container={true} spacing={0} alignItems="baseline">
        <Grid item={true} xs={12} sm={6}>
          <TitleWithIcon title={location.name} variant="h4">
            <Room />
          </TitleWithIcon>
        </Grid>
        <Grid item={true} xs={12} sm={6}>
          <DefaultButtons
            onClose={handleClose}
            onDelete={handleDelete}
            onSave={handleSave}
            isSaveButtonEnabled={isEmailValid}
            isDeleteButtonEnabled={location.id !== NewEntityId}
            additionalButtons={additionalButtons}
          />
        </Grid>
      </Grid>
      <Grid item={true}>
        <LocationFields
          location={location}
          onPropertyValueChanges={handleChange}
          onChangeEmailIsValid={handleEmailIsValid}
        />
      </Grid>
      <Grid item={true}>
        {location.id !== NewEntityId && (
          <RoomList
            rooms={location.rooms}
            onRoomSelected={handleRoomSelected}
            onStartAddingRoom={handleStartAddingRoom}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default LocationForm;
